// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Where the user was when their session ended, so signing back in returns them.
 *
 * Its own module rather than living beside the unauthenticated handler in
 * `main.tsx`, because the sign-in page has to read it and importing from the
 * entry point would close a cycle (main → App → SignInPage → main).
 *
 * Losing a session mid-task costs the user twice: the sign-in, and then finding
 * their way back to what was open. The second half is avoidable even when the
 * first is not.
 */

const RETURN_TO_KEY = 'carepoint-return-to';

/** Paths that must never be stored, or sign-in would bounce in a circle. */
const AUTH_PATHS = ['/signin', '/register', '/forgot-password', '/reset-password'];

export function rememberReturnTo(pathWithQuery: string): void {
  if (AUTH_PATHS.some((path) => pathWithQuery.startsWith(path))) {
    return;
  }
  try {
    sessionStorage.setItem(RETURN_TO_KEY, pathWithQuery);
  } catch {
    // Storage unavailable; the user lands on the default route instead.
  }
}

/**
 * The remembered route, consumed once.
 *
 * Read-and-clear, so a stale entry cannot redirect a later deliberate sign-in
 * to wherever a previous session happened to die. `sessionStorage` rather than
 * `localStorage` for the same reason — it should not outlive the tab.
 */
export function takeReturnTo(): string | undefined {
  try {
    const value = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    /*
     * Only ever an in-app path. A stored absolute URL would make this an open
     * redirect — an attacker who could write one key would get a credible
     * "session expired" bounce to their own sign-in page.
     */
    if (!value || !value.startsWith('/') || value.startsWith('//')) {
      return undefined;
    }
    return value;
  } catch {
    return undefined;
  }
}
